<template>
  <div class="internal-parts-listing">
    <v-layout class="my-2">
      <v-flex md3 class="d-flex mr-2">
        <v-autocomplete
          class="ml-1"
          hide-details
          clearable
          v-model.trim="filter_asset"
          :items="assetList"
          dense
          flat
          filled
          placeholder="Filter by Asset"
          item-color="cyan"
          :loading="partsLoading"
          :disabled="partsLoading"
          color="cyan"
          solo
          item-text="barcode"
          item-value="id"
          v-on:change="getParts()"
        >
          <template v-slot:no-data>
            <v-list-item>
              <v-list-item-title
                v-html="'No Asset(s) Found.'"
              ></v-list-item-title>
            </v-list-item>
          </template>
          <template v-slot:item="{ item }">
            <v-list-item-icon class="mr-3">
              <v-icon>mdi-home-map-marker</v-icon>
            </v-list-item-icon>
            <v-list-item-content class="text-ellipsis max-width-400px">
              <v-list-item-title
                class="text-capitalize font-weight-500 font-size-16"
                >{{ item.barcode }}
              </v-list-item-title>
            </v-list-item-content>
          </template>
        </v-autocomplete>
      </v-flex>
      <v-flex md5>
        <div class="d-flex">
          <v-text-field
            v-model.trim="search"
            v-on:click:clear="clearFilter('search')"
            clearable
            dense
            filled
            flat
            label="Search"
            hide-details
            solo
            :loading="partsLoading"
            :disabled="partsLoading"
            color="cyan"
            @keydown.enter="getParts()"
            @keydown.tab="getParts()"
          ></v-text-field>

          <v-btn
            class="mx-2 custom-bold-button white--text"
            color="cyan"
            tile
            depressed
            :disabled="partsLoading"
            v-on:click="getParts()"
          >
            <v-icon>mdi-magnify</v-icon>
          </v-btn>
          <v-btn
            class="mx-2 custom-bold-button white--text"
            depressed
            :disabled="partsLoading"
            v-on:click="getParts()"
            color="cyan white--text"
            ><v-icon>mdi-reload</v-icon></v-btn
          >
        </div>
      </v-flex>

      <v-flex md1> </v-flex>
    </v-layout>
    <ListingTable :columnCount="10" :rowData="rowData">
      <template v-slot:thead>
        <thead>
          <tr>
            <th class="simple-table-th">Part Image</th>
            <th class="simple-table-th">Part #</th>
            <th class="simple-table-th">Part Name</th>
            <th class="simple-table-th">Part Info</th>
            <th class="simple-table-th">Asset ID #</th>
            <th class="simple-table-th">Asset Name</th>
            <th class="simple-table-th">Visit #</th>
            <th class="simple-table-th">Part Quantity #</th>
            <th class="simple-table-th">Part Warranty #</th>
            <th class="simple-table-th">Part Warranty Details</th>
            <th class="simple-table-th">Status</th>
            <th class="simple-table-th">Created Time</th>
          </tr>
        </thead>
        <tbody>
          <template v-if="rowData.length">
            <tr
              v-for="(row, index) in rowData"
              :key="index"
              v-on:click="routeToPart(row)"
              class="alternate-listing-row"
            >
              <td class="simple-table-td">
                <template>
                  <v-avatar size="40" class="ml-5">
                    <v-img
                      class="customer-image"
                      width="40"
                      height="40"
                      contain
                      :lazy-src="$defaultImage"
                      :src="$assetAPIURL(row.original_product?.part_image)"
                    >
                    </v-img>
                  </v-avatar>
                </template>
              </td>

              <td class="simple-table-td">
                <Barcode
                  :barcode="row && row.original_product_barcode"
                ></Barcode>
              </td>

              <td class="simple-table-td">
                <p
                  class="m-0 custom-nowrap-ellipsis"
                  v-if="getProductName(row)"
                >
                  <b>Name: </b>
                  {{ getProductName(row) }}
                </p>

                <p
                  class="m-0 custom-nowrap-ellipsis"
                  v-if="getProductName(row)"
                >
                  <b>Part Number: </b>
                  <span v-if="getProductNumber(row) !== 'Not Available!'">
                    {{ getProductNumber(row) }}
                  </span>
                  <em v-else class="text--secondary">Not Available</em>
                </p>
              </td>

              <td class="simple-table-td">
                <p class="m-0 custom-nowrap-ellipsis">
                  <b>Brand: </b>
                  <span v-if="getProductInfoBrand(row) != null">
                    {{ getProductInfoBrand(row) }}
                  </span>
                  <em v-else class="text--secondary">No brand</em>
                </p>

                <p class="m-0 custom-nowrap-ellipsis">
                  <b>Category: </b>
                  <span v-if="getProductCategoryName(row) != null">
                    {{ getProductCategoryName(row) }}
                  </span>
                  <em v-else class="text--secondary">No category</em>
                </p>
              </td>

              <td class="simple-table-td">
                <p class="m-0 custom-nowrap-ellipsis">
                  <template v-if="lodash.isEmpty(row.asset_barcode)"
                    ><em class="text--secondary">No Asset Found</em></template
                  >
                  <template v-else>
                    <div
                      style="text-align: center"
                      class="me-2"
                      @click.stop="routeToAsset(row.asset_id)"
                    >
                      <Barcode :barcode="row.asset_barcode"></Barcode>
                    </div>
                  </template>
                </p>
              </td>
              <td class="simple-table-td">
                <p class="m-0 custom-nowrap-ellipsis">
                  <template v-if="lodash.isEmpty(row.asset_name)"
                    ><em class="text--secondary">No Asset Found</em></template
                  >
                  <template v-else>{{ row.asset_name }}</template>
                </p>
              </td>

              <td class="simple-table-td">
                <p class="m-0 custom-nowrap-ellipsis">
                  <template v-if="lodash.isEmpty(row.visit_barcode)"
                    ><em class="text--secondary">Not Found</em></template
                  >
                  <template v-else>
                    <div
                      style="text-align: center"
                      class="me-2"
                      @click.stop="routeToTicket(row.visit_id)"
                    >
                      <v-chip color="red" small label outlined class="p-3">{{
                        row.visit_barcode
                      }}</v-chip>
                      <!-- <Barcode :barcode="row.visit_barcode"></Barcode> -->
                    </div>
                  </template>
                </p>
              </td>

              <td class="simple-table-td">
                <p
                  class="mb-1 font-size-15 custom-nowrap-ellipsis-count d-flex  justify-center"
                >
                   <b>{{
                      getProductQuantity(row) != null
                        ? getProductQuantity(row)
                        : 0
                    }}</b>
                </p>
              </td>

              <td class="simple-table-td">
                <p class="m-0 custom-nowrap-ellipsis">
                  <template v-if="lodash.isEmpty(row.warranty_barcode)"
                    ><em class="text--secondary"
                      >No Warranty Found</em
                    ></template
                  >
                  <template v-else>
                    <Barcode
                      class="ml-1 p-4"
                      small
                      :barcode="row.warranty_barcode"
                      v-if="row && row.warranty_barcode"
                    ></Barcode>
                  </template>
                </p>
              </td>
              <td class="simple-table-td">
                <!-- the fields need to be dynamic here -->
                <p class="m-0 custom-nowrap-ellipsis">
                  <b>Start Date: </b>
                  <template v-if="row.warranty_start_date">{{
                    formatDate(row.warranty_start_date)
                  }}</template>
                  <template v-else
                    ><em class="text--secondary">No Start Date</em></template
                  >
                </p>
                <p class="m-0 custom-nowrap-ellipsis">
                  <b>End Date: </b>
                  <template v-if="lodash.isEmpty(row.warranty_end_date)"
                    ><em class="text--secondary">No End Date</em></template
                  >
                  <template v-else>{{ formatDate(row.warranty_end_date) }}</template>
                </p>

                <p class="m-0 custom-nowrap-ellipsis">
                  <b>Duration </b>
                  <template v-if="lodash.isEmpty(row.warranty_duration)"
                    ><em class="text--secondary">No Duration </em></template
                  >
                  <template v-else
                    >{{ row.warranty_duration }}
                    {{ row.warranty_duration_type }}</template
                  >
                </p>
              </td>

              <td class="simple-table-td">
                <div>
                  <v-chip
                    label
                    :color="row?.original_product_status == 1 ? 'green' : 'red'"
                    text-color="white"
                    class="ml-2 p-4 text-uppercase font-weight-600"
                    small
                    v-if="row?.original_product_status == 1"
                  >
                    {{
                      row?.original_product_status == 1 ? "Active" : "Inactive"
                    }}
                  </v-chip>
                </div>
              </td>

              <td class="simple-table-td">
                <TableActivity v-if="!lodash.isEmpty(row.added_by)" :data="row">
                  <template v-slot:display_name>
                    {{ row.added_by.display_name }}
                  </template>
                  <template v-slot:date_time>
                    {{ formatDateTime(row.added_at) }}
                  </template>
                  <template v-slot:format_date_time>
                    {{ formatDateTime(row.added_at) }}
                  </template>
                </TableActivity>
              </td>
            </tr>
          </template>
          <template v-else>
            <tr>
              <td colspan="11">
                <p
                  class="m-0 row-not-found text-center font-weight-500 font-size-16"
                >
                  <img
                    :src="$assetURL('media/error/empty.png')"
                    class="row-not-found-image"
                  />
                  Uhh... There are no parts at the moment.
                </p>
              </td>
            </tr>
          </template>
        </tbody>
      </template>
    </ListingTable>
    <ListingFooter
      :dataLoading="partsLoading"
      :showingFrom="showingFrom"
      :showingTo="showingTo"
      :totalRows="totalRows"
      :currentPage="currentPage"
      :totalPages="totalPages"
    ></ListingFooter>
  </div>
</template>

<script>
import Barcode from "@/view/pages/partials/Barcode.vue";
import CommonMixin from "@/core/plugins/common-mixin";
import ListingTable from "@/view/pages/partials/Listing-Table.vue";
import ListingFooter from "@/view/pages/partials/Listing-Footer.vue";
import { QUERY, GET } from "@/core/services/store/request.module";
import TableActivity from "@/view/pages/partials/Table-Activity.vue";

export default {
  mixins: [CommonMixin],
  data() {
    return {
      partsLoading: false,
      rowData: [],
      assetList: [],
      filter_asset: null,
      dates: [],
      currentPage: 1,
      status: "all",
      search: null,
      totalPages: 0,
      totalRows: 0,
      rowsOffset: 0,
    };
  },
  props: {
    type: {
      type: String,
      default: null,
    },
    typeId: {
      type: Number,
      default: 0,
    },
  },
  watch: {
    typeId() {
      this.$nextTick(() => {
        this.getParts();
        this.getAssets();
      });
    },
  },
  methods: {
    routeToDetailLink(id, type) {
      this.$router.push({
        name: type,
        params: { id },
      });
    },
    clearFilter(field) {
      const _this = this;
      switch (field) {
        case "search":
          _this.search = null;
          break;
        case "dates":
          _this.dates = [];
          break;
      }
      _this.getParts();
      _this.getAssets();
    },
    getParts() {
      const data = {
        current_page: this.currentPage,
        entity: 0,
        search: this.search,
        status: this.status,
        asset: this.filter_asset,
        customer: this.typeId,
        dates: this.dates,
      };

      if (this.partsLoading) {
        return false;
      }

      this.partsLoading = true;

      this.$store
        .dispatch(QUERY, { url: "customer/parts", data: data })
        .then(({ data }) => {
          this.totalPages = Math.ceil(data.total_rows / data.per_page);
          this.currentPage = data.current_page;
          this.rowData = data.rows;
          this.totalRows = data.total_rows;
          this.rowsOffset = data.offset;
        })
        .catch((error) => {
          this.logError(error);
        })
        .finally(() => {
          this.partsLoading = false;
        });
    },
    getAssets() {
      this.$store
        .dispatch(GET, { url: `setting/equipment-list` })
        .then(({ data }) => {
          this.assetList = data;
        })
        .catch((error) => {
          this.logError(error);
        });
    },
    routeToPart(row) {
      this.$nextTick(() => {
        this.$router.push(
          this.getDefaultRoute("part.detail", {
            params: {
              id: row.product,
            },
          })
        );
      });
    },
    routeToAsset(asset_id) {
      this.$nextTick(() => {
        this.$router.push(
          this.getDefaultRoute("client-equipment.detail", {
            params: {
              id: asset_id,
            },
          })
        );
      });
    },
    routeToTicket(visit_id) {
      this.$nextTick(() => {
        this.$router.push(
          this.getDefaultRoute("visit.detail", {
            params: {
              id: visit_id,
            },
          })
        );
      });
    },
    getProductName(row) {
      return this.lodash.isEmpty(row) === false ? row.title : "Not Available!";
    },
    getProductNumber(row) {
      if (this.lodash.isEmpty(row)) {
        return "Not Available!";
      }
      const prefix = row?.original_product_prefix;
      const number = row?.original_product_prefix_no;

      if (prefix && number) {
        return `${prefix}-${number}`;
      } else if (prefix) {
        return prefix;
      } else if (number) {
        return number;
      } else {
        return "Not Available!";
      }
    },

    getProductInfoBrand(row) {
      return this.lodash.isEmpty(row) === false
        ? row.original_product_brand
        : null;
    },
    getProductCategoryName(row) {
      return this.lodash.isEmpty(row) === false
        ? row.original_product_category
        : null;
    },
    getProductQuantity(row) {
      return this.lodash.isEmpty(row) === false
        ? `${row.quantity} ${
            row.quantity_type != null ? row.quantity_type : ""
          }`
        : null;
    },
  },
  computed: {
    showingFrom() {
      return this.lodash.isEmpty(this.rowData) === false
        ? this.rowsOffset + 1
        : 0;
    },
    showingTo() {
      return this.rowsOffset + (this.rowData ? this.rowData.length : 0);
    },
  },
  mounted() {
    this.getParts();
    console.log("mounted");

    this.getAssets();
  },
  components: {
    Barcode,
    TableActivity,
    ListingFooter,
    ListingTable,
  },
};
</script>
